// shared/authorTrust.ts
// The account status state machine — statuses, and which admin actions are
// legal from which status — shared verbatim between api/src/tables.ts (the
// source of truth for storage/validation) and api/src/adminAuthors.ts
// (gating) on one side, and src/auth.ts, src/Header.tsx, and
// src/AdminAuthorRow.tsx on the other, so the two sides can't drift apart.
// ─────────────────────────────────────────────────────────────────
// New registrations start at 'pending' and stay there until an admin
// approves them by hand (admin review is deliberately manual). There's no
// bootstrap path here for a first admin account; that's still a direct
// table edit, same as isAdmin always has been.
export const AUTHOR_STATUSES = ['pending', 'active', 'suspended', 'rejected'];
export function isValidAuthorStatus(status) {
    return AUTHOR_STATUSES.includes(status);
}
/**
 * Statuses from the retired newsletter-swap product's six-state trust model,
 * still sitting on existing Authors rows. Every one of them maps to
 * 'pending' (those accounts are re-approved by hand for the new product)
 * except the two that were already terminal decisions. Admins are handled
 * separately by the caller (see tables.ts's rowToAuthorRecord) so an admin
 * can't lock themselves out.
 */
const LEGACY_STATUS_MAP = {
    applied: 'pending',
    'provisional-give': 'pending',
    provisional: 'pending',
    full: 'pending',
    disabled: 'suspended',
    rejected: 'rejected',
};
/** Reads a stored status string, translating legacy values. Missing/unrecognized → 'pending' (default-deny). */
export function parseStoredStatus(raw) {
    if (raw !== undefined && isValidAuthorStatus(raw))
        return raw;
    return (raw !== undefined && LEGACY_STATUS_MAP[raw]) || 'pending';
}
export const AUTHOR_ACTIONS = {
    approve: { allowedFrom: ['pending'], to: 'active', label: 'Approve' },
    reject: { allowedFrom: ['pending'], to: 'rejected', label: 'Reject', requiresNote: true },
    suspend: { allowedFrom: ['active'], to: 'suspended', label: 'Suspend', requiresNote: true },
    reactivate: { allowedFrom: ['suspended', 'rejected'], to: 'active', label: 'Reactivate' },
};
/** Every action whose allowedFrom includes `status` — src/AdminAuthorRow.tsx renders one button per entry, in AUTHOR_ACTIONS' own key order. */
export function actionsAvailableFrom(status) {
    return Object.entries(AUTHOR_ACTIONS)
        .filter(([, rule]) => rule.allowedFrom.includes(status));
}
/**
 * Only 'active' accounts may use the app at all (assets, projects, jobs).
 * This is a default-deny check, not a per-feature allowlist — new features
 * should check this same predicate rather than add their own status check.
 */
export function isActiveMember(status) {
    return status === 'active';
}
// ── Per-account limits ─────────────────────────────────────────────
// Defaults applied when an Authors row has no override set. Admins can
// change them per account (see api/src/adminAuthors.ts's settings route).
export const DEFAULT_STORAGE_QUOTA_MB = 1024;
export const MAX_STORAGE_QUOTA_MB = 102_400;
/**
 * Daily limits, counted over the last 24 hours (rolling, so there's no
 * burst at midnight): final renders queued, and images or videos sent for an
 * AI description. Cancelled renders don't count; failed ones do. See
 * api/src/jobs.ts.
 */
export const DEFAULT_RENDERS_PER_DAY = 30;
export const DEFAULT_AI_IMAGES_PER_DAY = 200;
export const MAX_DAILY_LIMIT = 10_000;
export const DAILY_WINDOW_MS = 24 * 60 * 60 * 1000;
export function isValidDailyLimit(v) {
    return typeof v === 'number' && Number.isInteger(v) && v >= 0 && v <= MAX_DAILY_LIMIT;
}
